import { CellClickedEvent, GridReadyEvent, RowSelectedEvent, RowSelectionOptions } from '@ag-grid-community/core';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AgFns, GridOptionsExt, RowSelectionMultipleCheckbox, RowSelectionSingle } from 'app/shared/ag-fns';
import { takeUntil } from 'rxjs/operators';
import {
  Addon,
  AddonDoc,
  AddonStation,
  ItemDetailAddon,
  JoDetailAddon,
  ManufacturerProductTypeConfigAddon,
} from '../model/entities/entity-model';
import { DomainBaseComponent, wrapBusy } from '../shared/domain-base.component';
import { DomainService } from '../shared/domain.service';

import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { EditAddonDialogComponent } from './edit-addon-dialog.component';
import { UniAgFns } from '@/shared/uni-ag-fns';
import { agCharts } from '@ag-grid-enterprise/charts';

@Component({
  selector: 'app-addon',
  templateUrl: './addon.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddonComponent extends DomainBaseComponent implements OnInit {
  addonGridOptions!: GridOptionsExt;
  selectedAddon!: Addon;
  allowCombination: boolean = false;
  addonRowSelection: RowSelectionOptions = RowSelectionSingle;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.addonGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAddonGridReady,
      onRowSelected: this.onAddonRowSelected,
      onFilterChanged: this.onFilterChanged,
      rowModelType: 'serverSide',
      onFirstDataRendered: (e) => {
        this.AgFns.sizeColumnsToFit(this.addonGridOptions);
      },
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.replaceLocation();
        }
      },
    });
    // AgFns.captureGridRouteParams(this.addonGridOptions, this.route, "id");
    this.captureLocation(this.addonGridOptions, 'id');
    this.isPageReady = true;
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Addon);
    this.uow.clearEntities(AddonDoc);
    this.uow.clearEntities(AddonStation);
    return true;
  }

  onAddonGridReady(event: GridReadyEvent) {
    const colDefs = [
      this.productAdminAllow({
        ...AgFns.createButtonProps('', this.addonEdit.bind(this), {
          label: 'Edit',
        }),
        colId: 'edit',
        ...AgFns.fixWidth(80),
      }),
      this.productAdminAllow({
        ...AgFns.createButtonProps('', this.onCombine.bind(this), {
          label: 'Combine',
          canDisplay: this.canCombine.bind(this),
        }),
        colId: 'combine',
        ...AgFns.fixWidth(110),
      }),
      {
        headerName: 'Account',
        field: 'account.accountName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Name and Location',
        field: 'nameAndLocation',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Grouping Key',
        field: 'groupingKey',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Station',
        field: 'addonStation.name',
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Station Instructions',
        field: 'instructions',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(160),
      },
      {
        ...AgFns.createCheckboxProps('Hidden on Docs?', 'isHiddenOnDocs'),
        editable: false,
        ...AgFns.fixWidth(120),
      },
      {
        ...AgFns.createCheckboxProps("Needs Add'l Info?", 'needsAdditionalInfo'),
        editable: false,
      },
      {
        headerName: "Add'l Info Description",
        field: 'additionalInfoDescription',
        filter: 'agTextColumnFilter',
      },
      {
        ...AgFns.createCheckboxProps('Mismatch Add-on?', 'isFeatureMismatchAddon'),
        editable: false,
      },
      {
        headerName: '# Images',
        sortable: false,
        valueGetter: (params: any) => {
          const addon = <Addon>params.data;
          return addon && addon.addonImages.length;
        },
        ...AgFns.fixWidth(120),
      },
      { ...UniAgFns.getActiveStatusColDef('activeStatus.name'), ...AgFns.fixWidth(120) },
      UniAgFns.getEntityDeleteColDef(this.onDelete.bind(this), {
        canDisplay: () => true,
      }),
      // No longer supported.
      // this.invAllow({ ...AgFns.createButtonProps('', this.addonDelete.bind(this), { label: 'Delete' }) }),
      // { headerName: "Id", field: "id", filter: "agNumberColumnFilter" },
    ];
    // sorted on server.
    AgFns.initGrid(event, colDefs, null);
    this.updateAddonGridMode();

    this.updateDatasource();
  }

  onAllowCombinationChange() {
    this.updateAddonGridMode();
  }

  private updateAddonGridMode() {
    const gridApi = this.addonGridOptions.apiX;
    if (gridApi == null) return;
    gridApi.setColumnsVisible(['edit'], !this.allowCombination);
    gridApi.setColumnsVisible(['combine'], this.allowCombination);
    this.addonRowSelection = this.allowCombination ? RowSelectionMultipleCheckbox : RowSelectionSingle;
    gridApi.deselectAll();
  }

  canCombine(combineAddon: Addon) {
    if (this.addonGridOptions.apiX == null) return false;
    const selected = this.addonGridOptions.apiX.getSelectedRows() as Addon[];
    if (this.allowCombination && selected.length > 2) {
      this.toastr.success('Only two add-ons may be combined at one time.');
      this.addonGridOptions.apiX.deselectAll();
      return false;
    }
    if (combineAddon != this.selectedAddon || selected.length != 2) return false;
    // Don't allow combining across accounts or across stations (server enforces this too).
    const other = selected.find((x) => x != combineAddon);
    return other?.accountId === combineAddon.accountId && other?.addonStationId === combineAddon.addonStationId;
  }

  okToPerform(): boolean {
    if (this.uow.hasChanges()) {
      this.dialogService.showOkMessage('Cannot Continue', `You cannot perform this operation until other changes are first saved.`);
      return false;
    }
    return true;
  }

  @wrapBusy
  async onCombine(combineAddon: Addon, event?: CellClickedEvent, origEvent?: UIEvent) {
    // Stop propagation so the row-select event doesn't deselect the other checked row mid-flight.
    origEvent?.stopPropagation();

    if (!this.okToPerform()) return;
    if (combineAddon == null) return;

    const fromAddons = (this.addonGridOptions.apiX?.getSelectedRows() as Addon[]).filter((x) => x != combineAddon);
    if (fromAddons.length != 1) return;

    const ok = await this.dialogService.yesNoDialog('Combine Add-Ons', 'Are you sure that you want to combine these Add-Ons?');
    if (!ok) return;

    let count: number;
    await AgFns.busyGrid([this.addonGridOptions], this, async () => {
      count = await this.dbSaveService.combineAddons(fromAddons[0].id, combineAddon.id);
    });

    // Server mutated rows outside Breeze — clear cached entities so the next refresh sees fresh state.
    this.uow.clearEntities(Addon);
    this.uow.clearEntities(ItemDetailAddon);
    this.uow.clearEntities(JoDetailAddon);
    this.uow.clearEntities(ManufacturerProductTypeConfigAddon);

    this.addonGridOptions.apiX!.deselectAll();
    this.addonGridOptions.apiX!.refreshServerSide();
    this.toastr.success(`${count} records updated as a result of combining these add-ons`);
  }

  updateDatasource() {
    const gridApi = this.addonGridOptions.apiX;
    if (gridApi == null) {
      return;
    }
    const sortModel = [{ colId: 'account.accountName', sort: 'asc' as const }];

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createAddonQuery(), sortModel);

    gridApi.setGridOption('serverSideDatasource', ds);

    this.applyLocation();
  }

  productAdminAllow(x: any) {
    return this.user.isProductAdmin ? x : {};
  }

  async onAddonRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    const addon = e.data as Addon;
    if (!addon) {
      return;
    }
    if (this.allowCombination) {
      const selected = (this.addonGridOptions.apiX?.getSelectedRows() as Addon[]) ?? [];
      if (selected.length > 2) {
        e.node.setSelected(false);
        this.toastr.warning('Only two Add-Ons may be combined at one time.');
        return;
      }
      const others = selected.filter((x) => x != addon);
      const accountMismatch = others.find((x) => x.accountId !== addon.accountId);
      if (accountMismatch) {
        e.node.setSelected(false);
        this.toastr.warning('Add-Ons can only be combined within the same Account.');
        return;
      }
      const stationMismatch = others.find((x) => x.addonStationId !== addon.addonStationId);
      if (stationMismatch) {
        e.node.setSelected(false);
        this.toastr.warning('Add-Ons can only be combined within the same Station.');
        return;
      }
    }
    this.selectedAddon = addon;
    this.replaceLocation(addon.id);
  }

  onFilterChanged() {
    this.replaceLocation();
  }

  // -----------------------------------------------------
  @wrapBusy
  async onDelete(ao: Addon) {
    const _ao = await this.dbQueryService.getAddonExt(ao.id);
    if (_ao!.itemDetailAddons.length > 0 || _ao!.joDetailAddons.length > 0 || _ao!.manufacturerProductTypeConfigAddons.length > 0) {
      await this.dialogService.okDialog(
        'Cannot Delete',
        'Cannot delete Add-On that has already been used in item details, job order details or manufacturer product type configuations.',
      );
      return;
    }

    const ok = await this.dialogService.yesNoDialog('Delete this Add-On?', 'Are you sure?');
    if (!ok) return;

    if (_ao!.addonDocMaps.length > 0) {
      _ao!.addonDocMaps.forEach((p) => {
        p.entityAspect.setDeleted();
      });
    }

    if (_ao!.addonImages.length > 0) {
      _ao!.addonImages.forEach((p) => {
        p.entityAspect.setDeleted();
      });
    }

    _ao.entityAspect.setDeleted();
    const sr = await this.dbSaveService.saveChanges();
    if (sr.httpResponse.statusText == 'OK') {
      this.toastr.success('Add-On successfully deleted');
      this.updateDatasource();
    } else {
      this.toastr.error('Add-On delete failed', 'Unable to Delete');
    }
  }

  async addonAdd() {
    const r = await DialogBaseComponent.open(EditAddonDialogComponent, this, {});
    if (r) {
      this.addonGridOptions.apiX!.refreshServerSide();
    }
  }

  async addonEdit(addon: Addon) {
    const r = await DialogBaseComponent.open(EditAddonDialogComponent, this, {
      addon: addon,
    });
    if (r) {
      this.addonGridOptions.apiX!.refreshCells();
    }
  }

  // goAddonStations() {
  //   this.router.navigate(["/references/manage/process-stations"]);
  // }
}
